import { X, Trophy, Star, Calendar, Target, Flag } from "lucide-react";

// Consistent avatar color from a string id.
const AVATAR_COLORS = ["bg-amber-500", "bg-emerald-500", "bg-sky-500", "bg-rose-500", "bg-violet-500", "bg-cyan-500"];
const colorFor = (id) => {
  if (!id) return AVATAR_COLORS[0];
  return AVATAR_COLORS[(id.charCodeAt(0) + id.charCodeAt(id.length - 1)) % AVATAR_COLORS.length];
};
const initials = (s) => (s ? s.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) : "?");

const Stat = ({ icon: Icon, value, label, accent }) => (
  <div className="rounded-lg bg-background/60 p-3 text-center">
    <div className={`flex items-center justify-center gap-1 ${accent}`}>
      <Icon size={15} />
      <span className="text-lg font-extrabold">{value}</span>
    </div>
    <p className="mt-0.5 text-[11px] text-muted-foreground">{label}</p>
  </div>
);

/**
 * Football-only profile modal. Renders directly from the leaderboard ENTRY
 * (which already carries football team name, country and football points) so
 * NO cricket data is ever fetched or shown.
 */
export default function FootballProfileModal({ entry, onClose }) {
  if (!entry) return null;
  const name = entry.team_name || entry.display_name || "Player";
  const total = entry.total_points ?? entry.points ?? 0;
  const hasMd = typeof entry.match_days === "number";
  const hasMotd = typeof entry.manager_of_day_count === "number";
  const hasAcc = typeof entry.answered === "number" && entry.answered > 0;

  return (
    <div className="football-theme fixed inset-0 z-[65] grid place-items-center bg-black/70 p-4" onClick={onClose} data-testid="football-profile-modal">
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-card" onClick={(e) => e.stopPropagation()}>
        <div className="relative wc-hero-gradient p-6 pb-7 text-center">
          <button onClick={onClose} className="absolute right-3 top-3 text-white/80 hover:text-white" data-testid="football-profile-close" aria-label="Close">
            <X size={20} />
          </button>
          <div className={`mx-auto mb-3 grid h-20 w-20 place-items-center rounded-full ${colorFor(entry.user_id)} text-2xl font-extrabold text-white`}>
            {initials(entry.display_name || entry.team_name)}
          </div>
          <h3 className="truncate px-2 text-xl font-extrabold text-white">{name}</h3>
          <p className="mt-1 truncate px-2 text-sm text-white/75">{entry.display_name || "Manager"}</p>
          {entry.country && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-white/70"><Flag size={12} /> {entry.country}</p>
          )}
        </div>

        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={Trophy} value={`#${entry.rank}`} label="Rank" accent="wc-gold" />
            <Stat icon={Star} value={total} label="Total points" accent="text-emerald-400" />
          </div>
          {(hasMd || hasMotd || hasAcc) && (
            <div className="grid grid-cols-2 gap-3">
              {hasMd && <Stat icon={Calendar} value={entry.match_days} label="Match days" accent="text-sky-400" />}
              {hasMotd && <Stat icon={Trophy} value={entry.manager_of_day_count} label="Manager of the Day" accent="text-amber-400" />}
              {hasAcc && <Stat icon={Target} value={`${entry.accuracy}%`} label="Predictor accuracy" accent="text-violet-400" />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
